import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ImagePlus, Loader2, X, Eye, EyeOff } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Textarea with inline image upload support.
 * Images are inserted as [IMG:image_id] placeholders at cursor position.
 */
export default function ImageTextarea({ 
  value, 
  onChange, 
  placeholder = "Enter content...",
  rows = 10,
  className = ""
}) {
  const [uploading, setUploading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Invalid file type. Please use JPEG, PNG, GIF, or WebP.");
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File too large. Maximum size is 5MB.");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await axios.post(
        `${API}/academy/admin/upload-content-image`,
        formData,
        {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" }
        }
      );

      const { placeholder: imgPlaceholder } = response.data;
      
      // Insert placeholder at cursor position
      const textarea = textareaRef.current;
      if (textarea) {
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const newValue = value.substring(0, start) + imgPlaceholder + value.substring(end);
        onChange(newValue);
        
        // Set cursor position after placeholder
        setTimeout(() => {
          textarea.focus();
          textarea.setSelectionRange(start + imgPlaceholder.length, start + imgPlaceholder.length);
        }, 0);
      } else {
        // Append to end if no cursor position
        onChange(value + imgPlaceholder);
      }

      toast.success("Image uploaded! Placeholder inserted.");
    } catch (error) {
      console.error("Error uploading image:", error);
      toast.error(error.response?.data?.detail || "Failed to upload image");
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 mb-2">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp"
          onChange={handleImageUpload}
          className="hidden"
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</>
          ) : (
            <><ImagePlus className="w-4 h-4 mr-2" /> Insert Image</>
          )}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setShowPreview(!showPreview)}
        >
          {showPreview ? (
            <><EyeOff className="w-4 h-4 mr-2" /> Hide Preview</>
          ) : (
            <><Eye className="w-4 h-4 mr-2" /> Show Preview</>
          )}
        </Button>
        <span className="text-xs text-gray-500">
          (Max 5MB per image. JPEG, PNG, GIF, WebP)
        </span>
      </div>

      <Textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        className={className}
      />

      {showPreview && (
        <div className="mt-4 p-4 border rounded-lg bg-slate-800/50">
          <p className="text-sm font-medium text-gray-600 mb-2">Preview:</p>
          <ContentWithImages content={value} />
        </div>
      )}
    </div>
  );
}

/**
 * Renders content with [IMG:id] placeholders replaced by actual images.
 * Used for preview and display purposes.
 */
export function ContentWithImages({ content, className = "" }) {
  const [images, setImages] = useState({});
  const [loadingImages, setLoadingImages] = useState({});

  // Extract all image IDs from content
  const getImageIds = () => {
    const imagePattern = /\[IMG:([^\]]+)\]/g;
    const ids = [];
    let match;
    while ((match = imagePattern.exec(content || "")) !== null) {
      if (!ids.includes(match[1])) {
        ids.push(match[1]);
      }
    }
    return ids;
  };

  // Load images that haven't been loaded yet
  useEffect(() => {
    const imageIds = getImageIds();
    imageIds.forEach(async (imageId) => {
      if (!images[imageId] && !loadingImages[imageId]) {
        setLoadingImages(prev => ({ ...prev, [imageId]: true }));
        try {
          const response = await axios.get(`${API}/academy/content-image/${imageId}`);
          setImages(prev => ({ ...prev, [imageId]: response.data.data_url }));
        } catch (error) {
          console.error(`Error loading image ${imageId}:`, error);
          setImages(prev => ({ ...prev, [imageId]: null }));
        } finally {
          setLoadingImages(prev => ({ ...prev, [imageId]: false }));
        }
      }
    });
  }, [content]);

  // Replace placeholders with images or loading indicators
  const renderContent = () => {
    if (!content) return null;

    const parts = content.split(/(\[IMG:[^\]]+\])/g);
    
    return parts.map((part, index) => {
      const imgMatch = part.match(/\[IMG:([^\]]+)\]/);
      if (imgMatch) {
        const imageId = imgMatch[1];
        const imageUrl = images[imageId];
        const isLoading = loadingImages[imageId];

        if (isLoading) {
          return (
            <div key={index} className="inline-flex items-center justify-center w-24 h-24 bg-slate-800 rounded my-2">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          );
        }

        if (imageUrl) {
          return (
            <img
              key={index}
              src={imageUrl}
              alt="Content image"
              className="max-w-full h-auto rounded-lg my-2 shadow-sm"
              style={{ maxHeight: "400px" }}
            />
          );
        }

        // Image not found or failed to load
        return (
          <div key={index} className="inline-flex items-center justify-center px-3 py-1 bg-red-500/20 text-red-600 rounded text-sm my-2">
            <X className="w-4 h-4 mr-1" /> Image not found
          </div>
        );
      }

      // Regular text - preserve whitespace
      return <span key={index} className="whitespace-pre-wrap">{part}</span>;
    });
  };

  return (
    <div className={`text-gray-700 leading-relaxed ${className}`}>
      {renderContent()}
    </div>
  );
}
